'use client'

import classNames from 'clsx'
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import Loader from '@/ui/Loader'

import type {
  LeaderboardBlock,
  LeaderboardSelfRow
} from '../usePagedLeaderboard'
import S from './LeaderboardBoard.module.scss'
import { type MyRankDirection, MyRankPill } from './MyRankPill'

export type RowRenderState = { isSelf: boolean; highlighted: boolean }

export type MyRankPillContent = {
  visual: ReactNode
  label: string
  rank: number
  value: string
  unit?: string
}

type LeaderboardBoardProps<TRow> = {
  header: ReactNode
  blocks: LeaderboardBlock<TRow>[]
  getRowKey: (row: TRow) => string
  renderRow: (row: TRow, state: RowRenderState) => ReactNode
  isSelfRow?: (row: TRow) => boolean
  selfRow?: LeaderboardSelfRow<TRow>
  selfLabel: string
  /** Keeps the pinned own row at the bottom of a scrolling container. */
  stickySelf?: boolean
  /** Absent for guests and when the viewer is outside the selection. */
  pill?: MyRankPillContent
  /** Loads the range around the viewer's rank. */
  onRevealSelf?: () => Promise<void>
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
  onLoadPrevious: (blockIndex: number) => void
  labels: { loadMore: string; showHigher: string; jump: string }
}

const HIGHLIGHT_MS = 1600

type ViewportPosition = 'visible' | MyRankDirection

function useViewportPosition(element: HTMLElement | null) {
  const [state, setState] = useState<{
    element: HTMLElement
    position: ViewportPosition
  }>()

  useEffect(() => {
    if (!element || typeof IntersectionObserver === 'undefined') {
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      const position: ViewportPosition = entry.isIntersecting
        ? 'visible'
        : entry.boundingClientRect.top < 0
        ? 'up'
        : 'down'
      setState({ element, position })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return state?.element === element ? state.position : undefined
}

export function LeaderboardBoard<TRow>({
  header,
  blocks,
  getRowKey,
  renderRow,
  isSelfRow,
  selfRow,
  selfLabel,
  stickySelf = false,
  pill,
  onRevealSelf,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onLoadPrevious,
  labels
}: LeaderboardBoardProps<TRow>) {
  const [selfElement, setSelfElement] = useState<HTMLElement | null>(null)
  const [endElement, setEndElement] = useState<HTMLElement | null>(null)
  const [highlighted, setHighlighted] = useState(false)
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Set while the range around the own rank loads: the own row scrolls into
  // view as soon as it mounts inside that range.
  const scrollOnMount = useRef(false)
  const position = useViewportPosition(selfElement)
  const endPosition = useViewportPosition(endElement)

  const showSelf = useCallback((element: HTMLElement) => {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(true)
    clearTimeout(highlightTimer.current)
    highlightTimer.current = setTimeout(
      () => setHighlighted(false),
      HIGHLIGHT_MS
    )
  }, [])

  useEffect(() => () => clearTimeout(highlightTimer.current), [])

  const attachListSelf = useCallback(
    (element: HTMLLIElement | null) => {
      setSelfElement(element)
      if (element && scrollOnMount.current) {
        scrollOnMount.current = false
        showSelf(element)
      }
    },
    [showSelf]
  )

  const jumpToSelf = async () => {
    if (selfRow?.inList && selfElement) {
      showSelf(selfElement)
      return
    }
    if (onRevealSelf) {
      scrollOnMount.current = true
      await onRevealSelf()
    }
  }

  const renderListRow = (row: TRow) => {
    const isSelf = Boolean(isSelfRow?.(row))
    return (
      <li
        key={getRowKey(row)}
        ref={isSelf ? attachListSelf : undefined}
        className={S.item}
      >
        {renderRow(row, { isSelf, highlighted: isSelf && highlighted })}
      </li>
    )
  }

  const pinned = selfRow && !selfRow.inList ? selfRow.row : undefined
  // The pill floats only over the list: once its end is on screen the pill
  // would cover "Load more" and the footer.
  const pillDirection =
    position && position !== 'visible' && endPosition === 'down'
      ? position
      : null

  return (
    <div className={S.board}>
      <div className={S.header} aria-hidden>
        {header}
      </div>

      <ol className={S.list}>
        {blocks.map((block, index) => (
          <Fragment key={block.firstPage}>
            {index > 0 ? (
              <li className={S.gap}>
                <span className={S.gapDots} aria-hidden>
                  ···
                </span>
                <button
                  type="button"
                  className={S.gapButton}
                  onClick={() => onLoadPrevious(index)}
                >
                  {labels.showHigher}
                </button>
              </li>
            ) : null}
            {block.rows.map(row => renderListRow(row))}
          </Fragment>
        ))}
      </ol>

      {pinned ? (
        <section
          className={classNames(S.selfSection, stickySelf && S.selfSticky)}
          aria-label={selfLabel}
        >
          <p className={S.selfLabel}>{selfLabel}</p>
          <div ref={setSelfElement}>
            {renderRow(pinned, { isSelf: true, highlighted: false })}
          </div>
        </section>
      ) : null}

      <div ref={setEndElement} aria-hidden />

      {hasMore ? (
        <div className={S.loadMore}>
          <button
            type="button"
            className={S.loadMoreButton}
            onClick={onLoadMore}
            disabled={isFetchingMore}
          >
            {labels.loadMore}
          </button>
        </div>
      ) : null}

      {pill && pillDirection ? (
        <MyRankPill
          direction={pillDirection}
          visual={pill.visual}
          label={pill.label}
          rank={pill.rank}
          value={pill.value}
          unit={pill.unit}
          jumpLabel={labels.jump}
          onJump={jumpToSelf}
        />
      ) : null}
    </div>
  )
}

/** Holds the height of a first page while a board loads, so switching boards
 * does not collapse the page and pull the footer up. */
export function LeaderboardLoading() {
  return (
    <div className={S.loading}>
      <Loader variant="inline" />
    </div>
  )
}
